import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

const ANOMALY_STYLE = {
  MISSING_OUT:   { color: "#f87171", label: "Missing Out"   },
  LATE:          { color: "#fbbf24", label: "Late Arrivals" },
  SHORT_HOURS:   { color: "#fb923c", label: "Short Hours"   },
  NO_SHOW:       { color: "#f43f5e", label: "No Show"       },
  OVERTIME:      { color: "#60a5fa", label: "Overtime"      },
};

const STATUS_COLORS = {
  present: "#00ff90", wfh: "#60a5fa", absent: "#f87171",
  leave: "#fbbf24", late: "#fb923c",
};

export default function AttendanceLeave({ loading, data }) {
  const anomalies  = data?.anomalies  || [];
  const todayBreak = data?.todayBreakdown || null; // {present, wfh, absent, leave, late}
  const weekTrend  = data?.weekTrend  || []; // [{day, present, absent, wfh}]

  const donutData = todayBreak ? Object.entries(todayBreak)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k, value: v, color: STATUS_COLORS[k] || "#ffffff33" }))
    : [];

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Attendance & Leave</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Attendance & Leave — Today</span>
      </div>
      <div className="p-5 space-y-5">

        {/* Today's breakdown donut */}
        {donutData.length > 0 && (
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={110} height={110}>
              <PieChart>
                <Pie data={donutData} cx="50%" cy="50%" innerRadius={28} outerRadius={48} paddingAngle={2} dataKey="value">
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.85} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v} people`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 grid grid-cols-2 gap-1.5">
              {donutData.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-xs text-white/50 capitalize">{d.name}</span>
                  <span className="text-xs font-medium ml-auto" style={{ color: d.color }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Week trend stacked bar */}
        {weekTrend.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">This Week</p>
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={weekTrend} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="present" stackId="a" fill="#00ff90" fillOpacity={0.7} />
                <Bar dataKey="wfh"     stackId="a" fill="#60a5fa" fillOpacity={0.7} />
                <Bar dataKey="absent"  stackId="a" fill="#f87171" fillOpacity={0.7} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Anomalies */}
        {anomalies.length > 0 && (
          <div>
            <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Anomalies</p>
            <div className="space-y-1.5">
              {anomalies.map((x, i) => {
                const cfg = ANOMALY_STYLE[x.type] || { color: "#ffffff44", label: x.type };
                return (
                  <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 border border-white/5 bg-white/3">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
                      <span className="text-xs text-white/60">{cfg.label}</span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: cfg.color }}>{x.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}